import React, { useCallback, useEffect, useRef } from "react";

import { useIssueViewDialogStateOptional } from "#app/components/issue-view/issue-view-dialog-state";
import { useIssueFiltersOptional } from "#app/lib/filters/issue-filter-context";
import { useViews, useViewsOptional, type ViewPanelPage } from "#app/lib/views/view-context";
import {
  ViewOptionsMain,
  type ViewOptionsGroupBy,
  type ViewOptionsSortBy,
} from "./view-options-main";
import { OptionsPanelShell } from "./options-panel-shell";
import { ViewFilters } from "./view-filters";
import { ViewOptionsMore } from "./view-options-more";
import { ViewWorkflows } from "./view-workflows";

/**
 * The Customize view panel for the mounted saved view: a non-blocking Sheet
 * pinned to the view pane on desktop, a Drawer on mobile.
 *
 * THE VIEW BODY RENDERS THIS, not the route. The Fields page is made of
 * things only the body has — a list's column roster, a board's card fields —
 * and handing those up through a registration channel is a stale render
 * waiting to happen. The body already holds them, so it passes them down as
 * `fields` and `groupBy`. The Filters page reads the issue screen's filter
 * config off `IssueFilterProvider`, so it needs nothing passed; the Workflows
 * page (a workspace dashboard's source set) reads the view row itself.
 * Open/close state stays in `ViewsContext`, so any trigger anywhere can open
 * the panel.
 */

const PAGE_TITLES: Record<ViewPanelPage, string> = {
  main: "Customize view",
  fields: "Fields",
  filters: "Filters",
  more: "More options",
  workflows: "Workflows",
};

export type ViewOptionsFields = {
  editor: React.ReactNode;
  shownCount: number;
};

export type ViewOptionsPanelProps = {
  /** The below-tabs region to pin the desktop sheet inside. */
  anchorId?: string | undefined;
  /** The grouping dimensions this body offers. Omitted, the row is hidden. */
  groupBy?: ViewOptionsGroupBy | undefined;
  /** The sort fields this body offers. Omitted, the row is hidden. */
  sortBy?: ViewOptionsSortBy | undefined;
  /** Body-contributed rows for the main page's Display group — a board's
   *  card-wide layout modes. Omitted, the group holds the shared rows only. */
  display?: React.ReactNode | undefined;
  /** The body's Fields editor — a list's column settings, a board's card
   *  fields — with how many it shows (the title field aside), which the main
   *  page's Fields row reads out. Omitted, the row is hidden. */
  fields?: ViewOptionsFields | undefined;
  /** Body-owned option groups rendered on the main page between the shared
   *  groups and Actions — a dashboard's Viewing/Layout sections. The node
   *  carries its own separators BETWEEN its sections; the main page owns the
   *  ones around the block. */
  sections?: React.ReactNode | undefined;
};

/** Renders nothing outside a saved-view scope (the team and member screens). */
export function ViewOptionsPanel(props: ViewOptionsPanelProps) {
  const views = useViewsOptional();
  if (!views) return null;
  return <ViewOptionsPanelInner {...props} />;
}

function ViewOptionsPanelInner({
  anchorId,
  groupBy,
  sortBy,
  display,
  fields,
  sections,
}: ViewOptionsPanelProps) {
  const {
    activeView,
    panelOpen,
    panelPage,
    openPanel,
    closePanel,
    setPanelPage,
    setPanelLeaveGuard,
  } = useViews();
  const filterCtx = useIssueFiltersOptional();
  // Both panels pin to the same below-tabs region, so they would cover each
  // other. Opening this one retires the filter drawer — and the project detail
  // drawer, whose side presentation covers the same region. Detail VIEW state only: a create draft is never closed
  // out from under the person filling it. The overlay contexts are read
  // through a ref so this fires only when the panel OPENS — a drawer the user
  // opens later, over the standing panel, is their newer intent and stays.
  const closeFilterPanel = filterCtx?.closeFilterPanel;
  const issueDialog = useIssueViewDialogStateOptional();
  const overlaysRef = useRef({ issueDialog });
  overlaysRef.current = { issueDialog };
  useEffect(() => {
    if (!panelOpen) return;
    closeFilterPanel?.();
    const overlays = overlaysRef.current;
    if (overlays.issueDialog?.state?.mode === "view") {
      overlays.issueDialog.closeIssue();
    }
  }, [panelOpen, closeFilterPanel]);

  // The shell asks to reopen when the toolbar toggle closed the panel over a
  // half-typed field and the reader chose to keep editing.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) openPanel(panelPage, "keep_editing");
      else closePanel();
    },
    [closePanel, openPanel, panelPage],
  );

  const openFields = useCallback(() => setPanelPage("fields"), [setPanelPage]);
  const openFilters = useCallback(
    () => setPanelPage("filters"),
    [setPanelPage],
  );
  const openMore = useCallback(() => setPanelPage("more"), [setPanelPage]);
  const back = useCallback(() => setPanelPage("main"), [setPanelPage]);

  // A sub-page whose content this body does not publish has nothing to show —
  // land on the main page rather than an empty panel. Filters needs the issue
  // screen's filter config, which a host outside an `IssueFilterProvider` (a
  // dashboard tab) does not have; Workflows is a workspace dashboard's page
  // only — a workflow's own views read from their workflow.
  // More options edits the shared issue-query config, so it follows the same
  // gate the main page's config groups do — a body that publishes none of
  // them (a dashboard) has nothing on the page.
  const hasConfigGroups = Boolean(groupBy || sortBy || display || fields);
  const page: ViewPanelPage =
    panelPage === "fields" && fields
      ? "fields"
      : panelPage === "filters" && filterCtx
        ? "filters"
        : panelPage === "more" && hasConfigGroups
          ? "more"
          : panelPage === "workflows" && activeView?.scopeKind === "workspace"
            ? "workflows"
            : "main";

  if (!activeView) return null;

  let body: React.ReactNode;
  if (page === "fields" && fields) {
    body = fields.editor;
  } else if (page === "filters" && filterCtx) {
    body = <ViewFilters config={filterCtx.config} />;
  } else if (page === "more") {
    body = <ViewOptionsMore />;
  } else if (page === "workflows") {
    body = <ViewWorkflows />;
  } else {
    body = (
      <ViewOptionsMain
        groupBy={groupBy}
        sortBy={sortBy}
        display={display}
        fieldsShownCount={fields?.shownCount}
        sections={sections}
        onOpenFields={openFields}
        onOpenFilters={openFilters}
        onOpenMore={openMore}
      />
    );
  }

  // NOTHING here writes. Every control drafts, the view previews it, and the
  // toolbar's save button counts what is pending — so this panel has no
  // footer and no commit of its own. A saved view is shared, so the one place
  // that commits it sits beside the view, not under whichever page of this
  // panel happens to be open.

  return (
    <OptionsPanelShell
      anchorId={anchorId}
      fieldsPage={page === "fields"}
      headerRung={page === "filters"}
      onBack={page === "main" ? undefined : back}
      onLeaveGuardChange={setPanelLeaveGuard}
      onOpenChange={handleOpenChange}
      open={panelOpen}
      title={PAGE_TITLES[page]}
    >
      {body}
    </OptionsPanelShell>
  );
}
